if (!window.Maximize.loadedScript.includes('bulk-order.js')) {
  window.Maximize.loadedScript.push('bulk-order.js');

  let _abortCtrl = null;
  let _debounceTimer = null;
  const _searchCache = {};

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {

      Alpine.data('xBulkOrder', (moneyFormat) => ({

        popupOpen: false,
        searchQuery: '',
        isSearching: false,
        searchResults: [],   // flat array of variant rows from API
        variantQtys: {},     // variantId → qty (popup stepper, persists)
        checkedVariants: {}, // variantId → boolean

        tableItems: [],      // [{variantId, productTitle, variantTitle, sku, image, priceFormatted, priceCents, available, qty}]
        isSubmitting: false,
        submitError: '',

        _cartDrawerId: null,
        _strings: {},
        _priceHiddenLabel: '', // pre-rendered hidden-price message (login / restricted), from server

        rowHover: {}, // rowId → transform string (directional hover-rect, single scope)

        onRowEnter(id) {
          this.rowHover = { ...this.rowHover, [id]: 'translateY(0)' };
        },
        onRowLeave(event, id) {
          const el = event.currentTarget;
          const { top, height } = el.getBoundingClientRect();
          const toBottom = event.clientY - top > height / 2;
          this.rowHover = { ...this.rowHover, [id]: toBottom ? 'translateY(101%)' : 'translateY(-101%)' };
        },
        rowHoverTransform(id) {
          return this.rowHover[id] || 'translateY(-101%)';
        },

        get hasSelected() {
          return Object.values(this.checkedVariants).some(Boolean);
        },
        get hasItems() {
          return this.tableItems.length > 0;
        },
        get searchStatusText() {
          if (this.isSearching) return this._strings.searching || '';
          if (!this.searchQuery.trim()) return '';
          const count = this.searchResults.length;
          if (count > 0) {
            const tmpl = this._strings.results_found || '';
            return tmpl.replace('[[COUNT]]', count);
          }
          const tmpl = this._strings.no_results || '';
          return tmpl.replace('__QUERY__', this.searchQuery);
        },

        init() {
          const stringsEl = this.$el.querySelector('[data-bulk-order-strings]');
          this._strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

          const labelEl = this.$el.querySelector('[data-bulk-order-price-hidden-label]');
          this._priceHiddenLabel = labelEl ? labelEl.innerHTML.trim() : '';

          const cartDrawerEl = document.getElementById('CartDrawer');
          this._cartDrawerId = cartDrawerEl ? cartDrawerEl.dataset.sectionId : null;
        },

        openPopup() {
          this.popupOpen = true;
          document.body.classList.add('overflow-y-hidden');
          this.$nextTick(() => this.$refs.popupInput?.focus());
        },

        closePopup() {
          this.popupOpen = false;
          document.body.classList.remove('overflow-y-hidden');
          this.searchQuery = '';
          this.searchResults = [];
          this.checkedVariants = {};
          if (_abortCtrl) { try { _abortCtrl.abort(); } catch(e) {} _abortCtrl = null; }
          clearTimeout(_debounceTimer);
          this.$nextTick(() => this.$refs.searchTrigger?.focus());
        },

        trapFocus(event) {
          const el = this.$refs.dialog;
          if (!el) return;
          const focusable = [...el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
          if (focusable.length < 2) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey) {
            if (document.activeElement === first) { event.preventDefault(); last.focus(); }
          } else {
            if (document.activeElement === last) { event.preventDefault(); first.focus(); }
          }
        },

        onSearchInput() {
          clearTimeout(_debounceTimer);
          if (this.searchQuery.trim()) {
            this.isSearching = true;
            this.searchResults = [];
          }
          _debounceTimer = setTimeout(() => this.fetchResults(this.searchQuery.trim()), 300);
        },

        async fetchResults(query) {
          this.checkedVariants = {};
          this.variantQtys = {}; // reset qty each new search (data still comes from cache)

          if (_abortCtrl) { try { _abortCtrl.abort(); } catch(e) {} _abortCtrl = null; }
          if (!query) { this.searchResults = []; return; }
          if (_searchCache[query]) { this.searchResults = _searchCache[query]; this.isSearching = false; return; }

          const ctrl = new AbortController();
          _abortCtrl = ctrl;
          this.isSearching = true;
          this.searchResults = [];

          try {
            // Hybrid: Predictive Search (SKU/variant title matching) + section rendering
            // Section runs can-hide-price server-side; title-matched products get all
            // variants via all_products[handle].variants in Liquid (no enrichment fetch needed).
            const url = Shopify.routes.root
              + 'search/suggest'
              + '?q=' + encodeURIComponent(query)
              + '&resources[type]=product'
              + '&resources[limit]=10'
              + '&resources[options][fields]=title,variants.title,variants.sku'
              + '&section_id=bulk-order-search-results';

            const res = await fetch(url, { signal: ctrl.signal });
            if (!res.ok) throw new Error('[BulkOrder] HTTP ' + res.status);

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const scriptEl = doc.getElementById('bulk-order-results-data');
            if (!scriptEl) { this.searchResults = []; return; }

            // Liquid v.price is in cents (integer); format client-side
            const rows = JSON.parse(scriptEl.textContent).map(r => ({
              ...r,
              variantId: String(r.variantId),
              priceFormatted: formatMoney(r.price, moneyFormat),
              compareAtPriceFormatted: (r.compareAtPrice && r.compareAtPrice !== r.price)
                ? formatMoney(r.compareAtPrice, moneyFormat)
                : '',
              priceCents: r.price,
            }));

            _searchCache[query] = rows;
            this.searchResults = rows;
          } catch (e) {
            if (e.name === 'AbortError') return;
            console.error('[BulkOrder] fetchResults error:', e);
          } finally {
            this.isSearching = false;
            _abortCtrl = null;
          }
        },

        toggleVariant(variantId) {
          this.checkedVariants = {
            ...this.checkedVariants,
            [variantId]: !this.checkedVariants[variantId],
          };
        },

        getPopupQty(variantId) {
          return this.variantQtys[variantId] ?? 1;
        },

        changePopupQty(variantId, delta) {
          const current = this.variantQtys[variantId] ?? 1;
          this.variantQtys = { ...this.variantQtys, [variantId]: Math.max(1, current + delta) };
        },

        setPopupQty(variantId, event) {
          const val = parseInt(event.target.value, 10);
          const qty = isNaN(val) || val < 1 ? 1 : val;
          event.target.value = qty;
          this.variantQtys = { ...this.variantQtys, [variantId]: qty };
        },

        clearSelected() {
          this.checkedVariants = {};
        },

        handleAddSelected() {
          for (const [variantId, checked] of Object.entries(this.checkedVariants)) {
            if (!checked) continue;
            const row = this.searchResults.find(r => r.variantId === variantId);
            if (!row) continue;

            const qty = this.variantQtys[variantId] ?? 1;
            const existing = this.tableItems.find(i => i.variantId === variantId);
            if (existing) {
              this.tableItems = this.tableItems.map(i =>
                i.variantId === variantId ? { ...i, qty: i.qty + qty } : i
              );
            } else {
              this.tableItems = [...this.tableItems, { ...row, qty }];
            }
          }
          this.checkedVariants = {};
          this.closePopup();
        },

        getItemTotal(item) {
          return formatMoney(item.priceCents * item.qty, moneyFormat);
        },

        getImgUrl(url) {
          if (!url) return '';
          const clean = url.replace(/_\d+x\d*(?:_crop_\w+)?(\.\w+)/, '$1');
          return clean.replace(/(\.\w+)(\?|$)/, '_240x240$1$2');
        },

        changeTableQty(variantId, delta) {
          this.tableItems = this.tableItems.map(i =>
            i.variantId === variantId ? { ...i, qty: Math.max(1, i.qty + delta) } : i
          );
        },

        setTableQty(variantId, event) {
          const val = parseInt(event.target.value, 10);
          const qty = isNaN(val) || val < 1 ? 1 : val;
          event.target.value = qty;
          this.tableItems = this.tableItems.map(i =>
            i.variantId === variantId ? { ...i, qty } : i
          );
        },

        removeItem(variantId) {
          this.tableItems = this.tableItems.filter(i => i.variantId !== variantId);
        },

        clearAll() {
          this.tableItems = [];
        },

        async handleSubmit() {
          if (!this.tableItems.length || this.isSubmitting) return;

          this.isSubmitting = true;
          this.submitError = '';

          const preorderKey = this._strings.preorder_key;
          const preorderMsg = this._strings.preorder_message;
          const items = this.tableItems.map(i => ({
            id: parseInt(i.variantId, 10),
            quantity: i.qty,
            ...(i.isPreorder && preorderKey && preorderMsg
              ? { properties: { [preorderKey]: preorderMsg } }
              : {}),
          }));

          const sectionsToRender = window.xCartHelper?.getSectionsToRender(this._cartDrawerId) || [];
          const sections = sectionsToRender.map(s => s.id);

          try {
            const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items, sections }),
            });
            const data = await res.json();

            if (data.status === 422) {
              this.submitError = data.description;
              return;
            }

            sectionsToRender.forEach(section => {
              const el = document.querySelector(section.selector);
              if (el && data.sections?.[section.id]) {
                el.innerHTML = getSectionInnerHTML(data.sections[section.id], section.selector);
              }
            });

            const cartDrawerEl = document.getElementById('CartDrawer');
            if (cartDrawerEl) cartDrawerEl.isOpen = true;

            window.xCartHelper?.updateCurrentCountItem();
            document.dispatchEvent(new CustomEvent(CART_EVENT.cartUpdate, {
              bubbles: true,
              detail: { isItemCountChanged: true },
            }));

            this.tableItems = [];
          } catch (e) {
            console.error('[BulkOrder]', e);
            this.submitError = this._strings.add_to_cart_failed || 'Failed to add to cart. Please try again.';
          } finally {
            this.isSubmitting = false;
          }
        },
      }));

    });
  });
}
